import { Injectable, Type, ComponentRef, Injector } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ModalDataService } from './modal-data.service';

export interface ModalConfig {
  component: Type<any>;
  data?: any;
  width?: string;
  height?: string;
  title?: string;
  closable?: boolean;
}

export interface ModalInstance {
  id: string;
  config: ModalConfig;
  componentRef?: ComponentRef<any>;
}

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private modals = new BehaviorSubject<ModalInstance[]>([]);
  private modalCounter = 0;
  private modalEvents = new Subject<any>();

  constructor(private injector: Injector) {}

  /**
   * Ouvre un modal avec le composant spécifié
   */
  open(config: ModalConfig): string {
    const modalId = `modal-${++this.modalCounter}`;

    // Stocker les données dans le service statique
    if (config.data) {
      ModalDataService.setData(config.data);
    }

    const modalInstance: ModalInstance = {
      id: modalId,
      config,
    };

    const currentModals = this.modals.value;
    currentModals.push(modalInstance);
    this.modals.next(currentModals);

    return modalId;
  }

  /**
   * Ferme un modal spécifique
   */
  close(modalId: string): void {
    const currentModals = this.modals.value;
    const modalIndex = currentModals.findIndex((modal) => modal.id === modalId);

    if (modalIndex !== -1) {
      // Détruire le composant si il existe
      const modal = currentModals[modalIndex];
      if (modal.componentRef) {
        modal.componentRef.destroy();
      }

      currentModals.splice(modalIndex, 1);
      this.modals.next(currentModals);

      // Nettoyer les données
      ModalDataService.clearData();
    }
  }

  /**
   * Ferme tous les modals
   */
  closeAll(): void {
    const currentModals = this.modals.value;
    currentModals.forEach((modal) => {
      if (modal.componentRef) {
        modal.componentRef.destroy();
      }
    });
    this.modals.next([]);
    ModalDataService.clearData();
  }

  /**
   * Retourne un observable des modals actifs
   */
  getModals(): Observable<ModalInstance[]> {
    return this.modals.asObservable();
  }

  /**
   * Émettre un événement modal
   */
  emitEvent(event: any): void {
    this.modalEvents.next(event);
  }

  /**
   * Écouter les événements modaux
   */
  getEvents(): Observable<any> {
    return this.modalEvents.asObservable();
  }
}
